import { useEffect } from 'react';
import { useQuery } from 'react-query';
import { dayjs, formatDate, type DateInput } from '@koob/dates';
import { ApiPollingStateEnum } from '@koob/enums';
import {
  getStayRoomsFilters,
  isRetryableError,
  tripFactsheetUrl,
} from '@koob/utils';
import { useTripDesignerConfig } from '../config/TripDesignerConfigProvider';
import { useExperiencePricingQuery } from './useExperiencePricingQuery';
import {
  useSetCompareVersionTotalPrice,
  useTripFilters,
} from './useTripStoreSelectors';
import {
  addToVersionTotal,
  priceVersionExperienceDay,
} from '../logic/priceVersionDay';

// A day of a version being compared prices itself and adds that price to the
// running total the compare panel shows. Each answer is added once: the
// effects below hang on the response, which react-query keeps the same object
// for as long as it is the same answer.

/** The price of an experience instance (on its first day) and its day extras. */
export function useVersionExperienceDayPrice({
  experience,
  experienceDayIndex,
  extras,
  date,
}: {
  experience: any;
  experienceDayIndex?: number;
  extras: any[];
  date: DateInput;
}) {
  const { getExperiencePricingFilters } = useTripDesignerConfig();
  const tripFilters: any = useTripFilters();
  const setCompareVersionTotal = useSetCompareVersionTotalPrice();
  const composition = tripFilters?.composition;

  const offset = experienceDayIndex ? experienceDayIndex - 1 : 0;
  const filters = {
    ...getExperiencePricingFilters?.(tripFilters),
    experiences: [experience?.id],
    experienceComposition: composition ?? undefined,
    startAt: dayjs(date).subtract(offset, 'days').toISOString(),
    endAt: dayjs(date)
      .subtract(
        experienceDayIndex ? experienceDayIndex - experience?.durationDays : 0,
        'days',
      )
      .toISOString(),
    isForTripSearch: true,
  };

  const { data, isLoading } = useExperiencePricingQuery({
    filters,
    locale: tripFilters?.locale ?? 'en',
    enabled: !!experience?.id,
  });

  const priced = data
    ? priceVersionExperienceDay(data?.data?.[0]?.price, {
        experienceDayIndex,
        extras,
        composition,
      })
    : null;

  useEffect(() => {
    if (priced)
      setCompareVersionTotal((prev: any) => addToVersionTotal(prev, priced));
    // Once per answer, not per render of the day.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return { price: priced, isLoading };
}

/** The price of one night at a hotel, polled while the search is in progress. */
export function useVersionHotelDayPrice({
  hotel,
  date,
  included,
  stayRooms,
}: {
  hotel: any;
  date: DateInput;
  included?: boolean;
  stayRooms: any;
}) {
  const { api } = useTripDesignerConfig();
  const tripFilters: any = useTripFilters();
  const setCompareVersionTotal = useSetCompareVersionTotalPrice();
  const locale = tripFilters?.locale ?? 'en';

  const filters = {
    hotels: [hotel?.id],
    startAt: formatDate(date, 'YYYY-MM-DD'),
    endAt: formatDate(dayjs(date).add(1, 'day'), 'YYYY-MM-DD'),
    rooms: getStayRoomsFilters(stayRooms, tripFilters?.composition),
    cities: [],
    countries: [],
    kinds: [],
    regions: [],
    styles: [],
    sustainability: null,
  };

  const query = useQuery(
    ['tripVersionHotelPrice', filters, locale],
    () => api.getHotelsAvailability({ filters, locale }),
    {
      enabled: !!hotel?.id && !included,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      // A 4xx fails identically on every replay: only a network error or a
      // 5xx is asked again.
      retry: (_count: number, error: unknown) => isRetryableError(error),
      retryDelay: 5000,
      refetchInterval: (answer: any) =>
        answer?.data?.state === ApiPollingStateEnum.IN_PROGRESS ? 3000 : false,
    },
  );

  const data: any = query.data;
  const isInProgress = data?.data?.state === ApiPollingStateEnum.IN_PROGRESS;
  const price = data?.data?.data?.[0];

  useEffect(() => {
    if (data && !isInProgress) {
      setCompareVersionTotal((prev: any) => addToVersionTotal(prev, price));
    }
    // Once per answer, not per render of the day.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return { price, isLoading: query.isFetching, isInProgress };
}

/**
 * Where the icon on a version's hotel opens, or nothing when the hotel is not
 * the app's own (a partner hotel has no factsheet to open).
 */
export function useVersionHotelFactsheetHref(hotel: any) {
  const { isOwnProduct, locale } = useTripDesignerConfig();
  const tripFilters: any = useTripFilters();

  return isOwnProduct(hotel)
    ? tripFactsheetUrl(`/hotels/${hotel?.id}`, {
        locale: tripFilters?.locale ?? locale,
        tripId: tripFilters?.tripId,
      })
    : undefined;
}
